/**
 * preview-capture — rasterize an HTML document to a base64 image for the
 * agent's `preview_screenshot` tool.
 *
 * This lives in @papan/collab/client because the export pipeline it reuses
 * (createCaptureSession + the snapdom/html2canvas rasterizer + export-error
 * reporting) is a set of RELATIVE-import siblings here. The agent-side handler
 * (features/agent/ui/previewScreenshotClient.ts) imports this one public entry
 * so the pipeline's internals stay unexported.
 *
 * The pipeline is 100% client-side and identical to the HTML→PDF/PNG export:
 *   - `snapshotFallback: false` → `html` is the already-serialized live-preview
 *     DOM (post-JavaScript, scripts stripped by the snapshot bridge).
 *   - `snapshotFallback: true`  → `html` is raw file source; createCaptureSession
 *     sanitizes it (script-strip) and renders static markup only.
 */
import { createCaptureSession, rgbToHex } from './export-capture.js'
import { rasterize } from './rasterizer/index.js'

// Re-export the export-error reporter so the agent handler reports failures on
// the SAME `src: 'export'` debug-log channel without a second package subpath.
export { reportExportError } from './export-errors.js'

/** Longest-edge cap (device px) for the returned image. ~1568px is the sweet
 *  spot for vision-model tiling efficiency; anything larger is downscaled at
 *  raster time (toCanvas scale), never re-encoded. */
export const DEFAULT_MAX_EDGE = 1568

/** First-screen height ≈ a 16:10 viewport for the given width (1280 → 800). */
const FIRST_SCREEN_RATIO = 0.625

export interface RasterizeHtmlOptions {
  /** Capture viewport width, CSS px. */
  width: number
  /** Full scrollable page (true) or just the first screen (false). */
  fullPage: boolean
  format: 'png' | 'jpeg'
  /** Parent folder id — injects `<base href="/preview/<parentId>/">` for
   *  cross-file resource resolution (images/CSS). Null skips it. */
  parentId: string | null
  /** Target file id — for export-error debug-log attribution only. */
  fileId: string
  /** true = raw source (sanitize + static); false = pre-serialized snapshot. */
  snapshotFallback: boolean
  /** Longest-edge cap, device px (default DEFAULT_MAX_EDGE). */
  maxEdge?: number
}

export interface RasterizedImage {
  /** Base64 (no data: prefix). */
  base64: string
  mime: string
  /** Final image dimensions, device px (post-downscale). */
  width: number
  height: number
  engine: string
}

/**
 * Render `html` to a base64 PNG/JPEG. Throws on failure (the caller reports via
 * reportExportError with the right stage). Composites onto the document's flat
 * background color so JPEG (no alpha) and body-transparent layouts still show
 * the page background.
 */
export async function rasterizeHtmlToImage(html: string, opts: RasterizeHtmlOptions): Promise<RasterizedImage> {
  const maxEdge = opts.maxEdge ?? DEFAULT_MAX_EDGE
  const mime = opts.format === 'jpeg' ? 'image/jpeg' : 'image/png'

  const session = await createCaptureSession(html, {
    parentId: opts.parentId,
    width: opts.width,
    snapshotFallback: opts.snapshotFallback,
  })
  try {
    const w = session.width()
    const contentH = session.contentHeight()
    // Region to keep: full content, or the first viewport-height screen.
    const regionH = opts.fullPage
      ? contentH
      : Math.min(contentH, Math.max(1, Math.round(w * FIRST_SCREEN_RATIO)))
    // Downscale so the longest edge fits under the vision cap (never upscale).
    const scale = Math.min(1, maxEdge / Math.max(1, Math.max(w, regionH)))
    const bg = session.bgInfo()

    // The rasterizer captures the WHOLE document (heightCss: contentH); toCanvas
    // clips to our region. Same call shape as the export pipeline (useExportSession).
    const source = await rasterize({ el: session.doc.documentElement, widthCss: w, heightCss: contentH }, { fileId: opts.fileId })
    try {
      const raster = await source.toCanvas({ x: 0, y: 0, w, h: regionH }, scale)
      // Composite onto the page background so transparency / JPEG look right.
      const out = document.createElement('canvas')
      out.width = raster.width
      out.height = raster.height
      const cx = out.getContext('2d')
      if (!cx) throw new Error('2D canvas context unavailable for screenshot compositing')
      cx.fillStyle = rgbToHex(bg.color[0], bg.color[1], bg.color[2])
      cx.fillRect(0, 0, out.width, out.height)
      cx.drawImage(raster, 0, 0)

      const dataUrl = out.toDataURL(mime, opts.format === 'jpeg' ? 0.9 : undefined)
      const base64 = dataUrl.includes(',') ? dataUrl.slice(dataUrl.indexOf(',') + 1) : dataUrl
      return { base64, mime, width: out.width, height: out.height, engine: source.engine }
    } finally {
      source.dispose()
    }
  } finally {
    session.dispose()
  }
}
